import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Paper, Button, TextField, MenuItem, RadioGroup, Radio,
  FormControlLabel, Stepper, Step, StepLabel, Card, CardContent, Dialog, DialogTitle,
  DialogContent, DialogActions, Divider, Chip, Alert, LinearProgress, Avatar,
  IconButton, Tooltip, Switch, Checkbox, FormGroup, Snackbar, FormLabel, FormControl,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import RateReviewIcon from '@mui/icons-material/RateReview';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import SaveIcon from '@mui/icons-material/Save';
import FileUpload from '../../components/common/FileUpload';
import { requestScreening, quickEligibilityHint } from '../../utils/screeningService';
import {
  LOAN_PURPOSES, CROSS_SELL_OPTIONS, EMPLOYMENT_TYPES, EMPLOYMENT_STATUSES,
  MALAYSIAN_STATES, DOCUMENT_REQUIREMENTS,
} from '../../data/loanPurposes';
import { PRODUCT_CATEGORIES, apiProductCatalog } from '../../data/productConfigData';
import db from '../../data/demoDatabase';
import { useAuth } from '../../context/AuthContext';

const PRIMARY = '#1B365D';
const SECONDARY = '#00B894';

const steps = [
  { label: 'Customer Details', icon: PersonIcon },
  { label: 'Documents', icon: UploadFileIcon },
  { label: 'Loan Configuration', icon: RequestQuoteIcon },
  { label: 'AI Screening & Products', icon: PsychologyIcon },
  { label: 'Review & Submit', icon: RateReviewIcon },
];

const initialCustomer = {
  fullName: '', icNumber: '', phone: '', email: '',
  gender: '', address: '', addressLine2: '', city: '', addressState: '', addressPostcode: '',
  state: '', postcode: '',
  employmentStatus: '', employmentType: '',
  workingVintage: '', monthlyIncome: '',
  companyName: '', position: '',
};

const initialLoanConfig = {
  loanAmount: '', useMaxEligible: false,
  tenure: 5, useMaxTenure: false,
  purpose: '', otherPurpose: '',
  allowCrossSell: false,
  crossSellOptions: [],
  processMode: 'ai_screening',
};

const screeningStatusColors = {
  approval: { bg: '#E8F5E9', color: '#2E7D32', label: 'PRE APPROVE', icon: CheckCircleIcon },
  refer: { bg: '#FFF8E1', color: '#F57F17', label: 'REFER', icon: HelpOutlineIcon },
  fail: { bg: '#FFEBEE', color: '#C62828', label: 'FAIL', icon: CancelIcon },
  direct_apply: { bg: '#E3F2FD', color: '#1565C0', label: 'DIRECT APPLY', icon: RequestQuoteIcon },
};

const formatRM = (n) => `RM ${Number(n || 0).toLocaleString('en-MY')}`;

export default function PartnerSubmission() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [activeStep, setActiveStep] = useState(0);
  const [customer, setCustomer] = useState(initialCustomer);
  const [loanConfig, setLoanConfig] = useState(initialLoanConfig);
  const [documents, setDocuments] = useState({}); // { docKey: [files] }

  const [screeningInProgress, setScreeningInProgress] = useState(false);
  const [screeningResult, setScreeningResult] = useState(null);
  const [selectedProductCodes, setSelectedProductCodes] = useState([]);

  const [pdpaConsent, setPdpaConsent] = useState(false);
  const [submittedCases, setSubmittedCases] = useState([]);
  const [successDialog, setSuccessDialog] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  const showSnack = (msg, severity = 'success') =>
    setSnack({ open: true, msg, severity });

  // ─── Save / Restore Draft ───────────────────────────────
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem('partner_submission_draft');
      if (saved) {
        const draft = JSON.parse(saved);
        if (window.confirm('Resume unsaved draft submission?')) {
          if (draft.customer) setCustomer(draft.customer);
          if (draft.loanConfig) setLoanConfig(draft.loanConfig);
          if (draft.activeStep !== undefined) setActiveStep(draft.activeStep);
        } else {
          sessionStorage.removeItem('partner_submission_draft');
        }
      }
    } catch (e) { /* ignore */ }
    // eslint-disable-next-line
  }, []);

  const handleSaveDraft = () => {
    sessionStorage.setItem('partner_submission_draft', JSON.stringify({
      customer, loanConfig, activeStep,
    }));
    showSnack('Draft saved — you can resume later');
  };

  // ─── Customer field changes ─────────────────────────────
  const updateCustomer = (field) => (e) => {
    const value = e?.target?.value !== undefined ? e.target.value : e;
    setCustomer((prev) => ({ ...prev, [field]: value }));
  };

  // ─── Employment group derivation ────────────────────────
  const employmentGroup = useMemo(() => {
    const t = EMPLOYMENT_TYPES.find((e) => e.key === customer.employmentType);
    return t?.group || 'salaried';
  }, [customer.employmentType]);

  const requiredDocs = DOCUMENT_REQUIREMENTS[employmentGroup] || DOCUMENT_REQUIREMENTS.salaried;

  // ─── Step validation ────────────────────────────────────
  const step1Valid = customer.fullName && customer.icNumber && customer.phone && customer.gender && customer.address && customer.city && customer.addressState && customer.addressPostcode && customer.monthlyIncome && customer.employmentType;

  const step2Valid = useMemo(() => {
    // Required documents must have at least 1 file
    const requiredOk = requiredDocs.filter((d) => d.required).every((d) => (documents[d.key]?.length || 0) > 0);
    if (!requiredOk) return false;
    // For self-employed: at least one of the oneOfGroup documents must be present
    if (employmentGroup === 'self_employed') {
      const oneOf = requiredDocs.filter((d) => d.oneOfGroup === 'self_employed_proof');
      const anyPresent = oneOf.some((d) => (documents[d.key]?.length || 0) > 0);
      if (!anyPresent) return false;
    }
    return true;
  }, [documents, requiredDocs, employmentGroup]);

  const step3Valid = (loanConfig.loanAmount || loanConfig.useMaxEligible) && loanConfig.purpose;

  const step4Valid = selectedProductCodes.length > 0;

  // ─── Step 4: Screening trigger ──────────────────────────
  useEffect(() => {
    if (activeStep === 3 && loanConfig.processMode === 'ai_screening' && !screeningResult && !screeningInProgress) {
      runScreening();
    }
    // eslint-disable-next-line
  }, [activeStep]);

  // Products this partner is entitled to submit (active commission entries)
  const assignedProductCodes = useMemo(
    () => db.users.getAssignedProductCodes(user?.id),
    [user]
  );

  const runScreening = async () => {
    setScreeningInProgress(true);
    setScreeningResult(null);
    try {
      const result = await requestScreening({
        customerProfile: { ...customer, employmentGroup },
        productCodes: assignedProductCodes, // gated to partner's assigned products
        loanAmount: Number(loanConfig.loanAmount) || (Number(customer.monthlyIncome) * 10),
        tenureMonths: (Number(loanConfig.tenure) || 5) * 12,
      });
      setScreeningResult(result);
    } catch (e) {
      showSnack('Screening service error — please try again', 'error');
    }
    setScreeningInProgress(false);
  };

  // ─── Eligibility hint ───────────────────────────────────
  const eligibilityHint = useMemo(
    () => quickEligibilityHint(customer, (Number(loanConfig.tenure) || 5) * 12),
    [customer, loanConfig.tenure]
  );

  // ─── Final submission ───────────────────────────────────
  const handleSubmit = () => {
    if (!pdpaConsent) {
      showSnack('Please confirm customer PDPA consent', 'warning');
      return;
    }
    const created = [];
    selectedProductCodes.forEach((productCode) => {
      const product = (screeningResult?.results || []).find((r) => r.productCode === productCode);
      const newCase = db.cases.create({
        applicantId: `CUST-${Date.now().toString().slice(-6)}-${Math.random().toString(36).slice(2, 5).toUpperCase()}`,
        applicantName: customer.fullName,
        productId: product?.productCode || productCode,
        productName: product?.productName || productCode,
        bank: product?.lenderName || 'TBD',
        amount: Number(loanConfig.loanAmount) || 0,
        status: 'Submitted',
        referralCode: user?.referralCode || null,
        referralType: 'partner',
        referralOwnerId: user?.id,
        submittedBy: user?.id,
        commissionAmount: 0,
        commissionStatus: 'pending',
      });
      db.auditLogs.create({
        userId: user?.id,
        userName: user?.name,
        userRole: user?.role,
        action: 'submit_application',
        entityType: 'case',
        entityId: newCase.id,
        description: `Submitted ${product?.productName || productCode} for ${customer.fullName}`,
        reason: `Loan amount: ${formatRM(loanConfig.loanAmount)} | Tenure: ${loanConfig.tenure}y | Purpose: ${loanConfig.purpose}`,
      });
      created.push(newCase);
    });
    setSubmittedCases(created);
    sessionStorage.removeItem('partner_submission_draft');
    setSuccessDialog(true);
  };

  // ─── Step navigation ────────────────────────────────────
  const handleNext = () => {
    if (activeStep === 0 && !step1Valid) {
      showSnack('Please fill in required customer details', 'warning');
      return;
    }
    if (activeStep === 1 && !step2Valid) {
      showSnack('Please upload all required documents', 'warning');
      return;
    }
    if (activeStep === 2 && !step3Valid) {
      showSnack('Please complete loan configuration', 'warning');
      return;
    }
    if (activeStep === 3 && !step4Valid) {
      showSnack('Please select at least one product to submit', 'warning');
      return;
    }
    setActiveStep((p) => p + 1);
  };

  const handleBack = () => setActiveStep((p) => Math.max(0, p - 1));

  const handleResetWizard = () => {
    setActiveStep(0);
    setCustomer(initialCustomer);
    setLoanConfig(initialLoanConfig);
    setDocuments({});
    setScreeningResult(null);
    setSelectedProductCodes([]);
    setPdpaConsent(false);
    setSubmittedCases([]);
    setSuccessDialog(false);
    sessionStorage.removeItem('partner_submission_draft');
  };

  // ─── Step 1: Customer Details ───────────────────────────
  const renderStep1 = () => (
    <Box>
      <Paper sx={{ p: 4, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: PRIMARY, mb: 3 }}>
          Customer Information
        </Typography>
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Full Name (as per IC) *" value={customer.fullName} onChange={updateCustomer('fullName')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="IC Number *" value={customer.icNumber} onChange={updateCustomer('icNumber')} placeholder="e.g. 900101011234" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Phone Number *" value={customer.phone} onChange={updateCustomer('phone')} placeholder="+60 12-345 6789" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Email" type="email" value={customer.email} onChange={updateCustomer('email')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth select size="small" label="Gender *" value={customer.gender} onChange={updateCustomer('gender')}>
              <MenuItem value="male">Male</MenuItem>
              <MenuItem value="female">Female</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth select size="small" label="Current State *" value={customer.state} onChange={updateCustomer('state')}>
              {MALAYSIAN_STATES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Postcode" value={customer.postcode} onChange={updateCustomer('postcode')} inputProps={{ maxLength: 5 }} />
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }}>
              <Chip label="Address" size="small" sx={{ fontWeight: 600 }} />
            </Divider>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Address Line 1 *" value={customer.address} onChange={updateCustomer('address')} placeholder="Street address, building, unit" />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Address Line 2" value={customer.addressLine2} onChange={updateCustomer('addressLine2')} placeholder="Apartment, suite, floor (optional)" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="City *" value={customer.city} onChange={updateCustomer('city')} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth select size="small" label="State *" value={customer.addressState} onChange={updateCustomer('addressState')}>
              {MALAYSIAN_STATES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="Postcode *" value={customer.addressPostcode} onChange={updateCustomer('addressPostcode')} inputProps={{ maxLength: 5 }} />
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }}>
              <Chip label="Employment" size="small" sx={{ fontWeight: 600 }} />
            </Divider>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth select size="small" label="Employment Status" value={customer.employmentStatus} onChange={updateCustomer('employmentStatus')}>
              {EMPLOYMENT_STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth select size="small" label="Employment Type *" value={customer.employmentType} onChange={updateCustomer('employmentType')}>
              {EMPLOYMENT_TYPES.map((t) => <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" type="number" label="Working Vintage (years)" value={customer.workingVintage} onChange={updateCustomer('workingVintage')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" type="number" label="Monthly Gross Income (RM) *" value={customer.monthlyIncome} onChange={updateCustomer('monthlyIncome')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Company Name" value={customer.companyName} onChange={updateCustomer('companyName')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Position" value={customer.position} onChange={updateCustomer('position')} />
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );

  // ─── Step 2: Documents ──────────────────────────────────
  const renderStep2 = () => {
    const requiredCount = requiredDocs.filter((d) => d.required).length;
    const uploadedRequiredCount = requiredDocs.filter((d) => d.required && (documents[d.key]?.length || 0) > 0).length;
    const progressPct = requiredCount === 0 ? 100 : Math.round((uploadedRequiredCount / requiredCount) * 100);

    return (
      <Paper sx={{ p: 4, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: PRIMARY }}>
            Document Upload
          </Typography>
          <Chip
            label={`${uploadedRequiredCount}/${requiredCount} required uploaded`}
            color={progressPct === 100 ? 'success' : 'default'}
            size="small"
            sx={{ fontWeight: 600 }}
          />
        </Box>
        <LinearProgress variant="determinate" value={progressPct} sx={{ mb: 2, height: 6, borderRadius: 3 }} />
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          {employmentGroup === 'salaried' && 'Salaried customers must provide IC and 3 months payslip. EPF and bank statements are recommended for stronger eligibility.'}
          {employmentGroup === 'self_employed' && 'Self-employed customers must provide IC, plus AT LEAST ONE of: 2-year income tax statement OR latest 6-month bank statement.'}
          {employmentGroup === 'commission' && 'Commission earners must provide IC and 6-month income statement. Bank statements strengthen the case.'}
          {!customer.employmentType && 'Please go back to Step 1 and select an employment type to see required documents.'}
        </Alert>

        <Grid container spacing={3}>
          {requiredDocs.map((doc) => (
            <Grid item xs={12} sm={6} key={doc.key}>
              <Box sx={{
                p: 2, borderRadius: 2, border: '1px solid',
                borderColor: doc.required && !documents[doc.key]?.length ? '#FFCDD2' : '#E0E0E0',
                backgroundColor: documents[doc.key]?.length ? '#F0FFF4' : 'transparent',
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{doc.label}</Typography>
                  {doc.required ? (
                    <Chip label="Required" size="small" sx={{ height: 20, fontSize: '0.65rem', backgroundColor: '#FFEBEE', color: '#C62828', fontWeight: 700 }} />
                  ) : (
                    <Chip label="Optional" size="small" sx={{ height: 20, fontSize: '0.65rem', backgroundColor: '#ECEFF1', color: '#546E7A', fontWeight: 700 }} />
                  )}
                </Box>
                <FileUpload
                  accept=".pdf,.jpg,.jpeg,.png"
                  multiple={doc.maxFiles > 1}
                  maxFiles={doc.maxFiles}
                  onFilesChange={(files) =>
                    setDocuments((prev) => ({ ...prev, [doc.key]: files }))
                  }
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>
    );
  };

  // ─── Step 3: Loan Configuration ─────────────────────────
  const renderStep3 = () => (
    <Paper sx={{ p: 4, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
      <Typography variant="h6" sx={{ fontWeight: 700, color: PRIMARY, mb: 3 }}>
        Loan Configuration
      </Typography>

      {/* Eligibility hint */}
      {eligibilityHint && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }} icon={<RequestQuoteIcon />}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>Quick Eligibility Estimate</Typography>
              <Typography variant="body2">
                Based on RM {Number(customer.monthlyIncome).toLocaleString()}/mth income, customer can afford ~{formatRM(eligibilityHint.affordableInstalment)}/mth instalment.
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" sx={{ display: 'block' }}>Estimated max loan ({eligibilityHint.tenureMonths / 12}y)</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: PRIMARY }}>
                {formatRM(eligibilityHint.estimatedMaxLoan)}
              </Typography>
            </Box>
          </Box>
        </Alert>
      )}

      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth size="small" type="number" label="Loan Amount (RM) *"
            value={loanConfig.useMaxEligible ? (eligibilityHint?.estimatedMaxLoan || '') : loanConfig.loanAmount}
            onChange={(e) => setLoanConfig((p) => ({ ...p, loanAmount: e.target.value, useMaxEligible: false }))}
            disabled={loanConfig.useMaxEligible}
          />
          <FormControlLabel
            sx={{ mt: 0.5 }}
            control={<Switch size="small" checked={loanConfig.useMaxEligible}
              onChange={(e) => setLoanConfig((p) => ({ ...p, useMaxEligible: e.target.checked, loanAmount: e.target.checked && eligibilityHint ? String(eligibilityHint.estimatedMaxLoan) : p.loanAmount }))} />}
            label={<Typography variant="caption">Use max eligible amount</Typography>}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth select size="small" label="Tenure (years) *"
            value={loanConfig.useMaxTenure ? 10 : loanConfig.tenure}
            onChange={(e) => setLoanConfig((p) => ({ ...p, tenure: Number(e.target.value), useMaxTenure: false }))}
            disabled={loanConfig.useMaxTenure}>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((y) => (
              <MenuItem key={y} value={y}>{y} year{y > 1 ? 's' : ''}</MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            sx={{ mt: 0.5 }}
            control={<Switch size="small" checked={loanConfig.useMaxTenure}
              onChange={(e) => setLoanConfig((p) => ({ ...p, useMaxTenure: e.target.checked, tenure: e.target.checked ? 10 : p.tenure }))} />}
            label={<Typography variant="caption">Use max tenure (10y)</Typography>}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth select size="small" label="Purpose of Loan *"
            value={loanConfig.purpose}
            onChange={(e) => setLoanConfig((p) => ({ ...p, purpose: e.target.value }))}>
            {LOAN_PURPOSES.map((p) => <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>)}
          </TextField>
        </Grid>
        {loanConfig.purpose === 'other' && (
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Specify other purpose"
              value={loanConfig.otherPurpose}
              onChange={(e) => setLoanConfig((p) => ({ ...p, otherPurpose: e.target.value }))} />
          </Grid>
        )}

        <Grid item xs={12}>
          <Box sx={{ p: 2, backgroundColor: '#F5F7FA', borderRadius: 2 }}>
            <FormControlLabel
              control={<Switch checked={loanConfig.allowCrossSell}
                onChange={(e) => setLoanConfig((p) => ({ ...p, allowCrossSell: e.target.checked }))} />}
              label={<Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Allow Cross-Sell to Alternative Products</Typography>
                <Typography variant="caption" color="text.secondary">If primary product fails eligibility, suggest alternative products to the customer</Typography>
              </Box>}
            />
            {loanConfig.allowCrossSell && (
              <Box sx={{ mt: 1.5, ml: 5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {CROSS_SELL_OPTIONS.map((opt) => {
                  const selected = loanConfig.crossSellOptions.includes(opt.key);
                  return (
                    <Chip
                      key={opt.key} label={opt.label} size="small"
                      onClick={() => setLoanConfig((p) => ({
                        ...p,
                        crossSellOptions: selected
                          ? p.crossSellOptions.filter((o) => o !== opt.key)
                          : [...p.crossSellOptions, opt.key],
                      }))}
                      sx={{
                        fontWeight: 600,
                        backgroundColor: selected ? PRIMARY : '#E0E0E0',
                        color: selected ? 'white' : 'inherit',
                        '&:hover': { backgroundColor: selected ? '#142847' : '#BDBDBD' },
                      }}
                    />
                  );
                })}
              </Box>
            )}
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
          <FormControl>
            <FormLabel sx={{ fontSize: '0.85rem', fontWeight: 700, color: PRIMARY, mb: 1 }}>
              Process Mode *
            </FormLabel>
            <RadioGroup value={loanConfig.processMode}
              onChange={(e) => setLoanConfig((p) => ({ ...p, processMode: e.target.value }))}>
              <FormControlLabel value="ai_screening"
                control={<Radio />}
                label={<Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>AI Screening (Recommended)</Typography>
                  <Typography variant="caption" color="text.secondary">Run customer profile through Fintos AI to find best-matched products with approval/refer/fail predictions</Typography>
                </Box>}
              />
              <FormControlLabel value="direct_apply"
                control={<Radio />}
                label={<Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Direct Apply (Skip Screening)</Typography>
                  <Typography variant="caption" color="text.secondary">Submit directly to all panel lenders without AI screening</Typography>
                </Box>}
              />
            </RadioGroup>
          </FormControl>
        </Grid>
      </Grid>
    </Paper>
  );

  // ─── Step 4: AI Screening + Product Selection ───────────
  const renderStep4 = () => {
    if (loanConfig.processMode === 'ai_screening' && screeningInProgress) {
      return (
        <Paper sx={{ p: 5, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', textAlign: 'center' }}>
          <Avatar sx={{ width: 72, height: 72, mx: 'auto', mb: 2, backgroundColor: '#FFF8E1' }}>
            <PsychologyIcon sx={{ fontSize: 40, color: '#F57F17' }} />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Running AI Screening...</Typography>
          <Chip label="In Process" size="small" sx={{ backgroundColor: '#FFF8E1', color: '#F57F17', fontWeight: 700, mb: 2 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Calling Fintos AI server to evaluate {customer.fullName}'s profile against all panel products...
          </Typography>
          <LinearProgress sx={{ borderRadius: 2, mb: 2 }} />
          <Typography variant="caption" color="text.secondary">Estimated time: ~30 seconds (demo: ~2.5s)</Typography>
        </Paper>
      );
    }

    // Direct apply: only the partner's assigned products (gating)
    const directApplyProducts = apiProductCatalog.filter((p) =>
      assignedProductCodes.includes(p.productCode)
    );

    const results = loanConfig.processMode === 'ai_screening'
      ? (screeningResult?.results || [])
      : directApplyProducts.map((p) => ({
          productCode: p.productCode,
          productName: p.productName,
          lenderName: p.lenderName,
          category: p.category,
          status: 'direct_apply',
          statusLabel: 'Direct Apply',
          score: null,
          reason: 'Skipped screening — direct apply mode',
        }));

    // Empty-state warning if the partner has no assigned products
    if (assignedProductCodes.length === 0) {
      return (
        <Paper sx={{ p: 5, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', textAlign: 'center' }}>
          <Avatar sx={{ width: 72, height: 72, mx: 'auto', mb: 2, backgroundColor: '#FFEBEE' }}>
            <CancelIcon sx={{ fontSize: 40, color: '#C62828' }} />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            No Products Assigned
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your account is not yet entitled to submit any product applications.
          </Typography>
          <Alert severity="warning" sx={{ borderRadius: 2, textAlign: 'left', maxWidth: 480, mx: 'auto' }}>
            Please contact iMoney admin to assign at least one product before submitting an application.
            Admin can configure this from <strong>Admin → Product &amp; Commission Configuration</strong>.
          </Alert>
        </Paper>
      );
    }

    // Group by category
    const grouped = {};
    results.forEach((r) => {
      if (!grouped[r.category]) grouped[r.category] = [];
      grouped[r.category].push(r);
    });

    const isSelectable = (status) => status !== 'fail';
    const allEligible = results.filter((r) => isSelectable(r.status));

    return (
      <Box>
        {/* Summary card */}
        {screeningResult && (
          <Paper sx={{ p: 2.5, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', mb: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ backgroundColor: '#E8F5E9', width: 40, height: 40 }}>
                  <CheckCircleIcon sx={{ color: '#2E7D32' }} />
                </Avatar>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>Screening Completed</Typography>
                  <Typography variant="caption" color="text.secondary">
                    ID: {screeningResult.screeningId} | {new Date(screeningResult.completedAt).toLocaleString()}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip label={`Pre Approve: ${screeningResult.summary.approval}`} size="small" sx={{ backgroundColor: '#E8F5E9', color: '#2E7D32', fontWeight: 700 }} />
                <Chip label={`Refer: ${screeningResult.summary.refer}`} size="small" sx={{ backgroundColor: '#FFF8E1', color: '#F57F17', fontWeight: 700 }} />
                <Chip label={`Fail: ${screeningResult.summary.fail}`} size="small" sx={{ backgroundColor: '#FFEBEE', color: '#C62828', fontWeight: 700 }} />
              </Box>
            </Box>
          </Paper>
        )}

        {/* Bulk action bar */}
        <Paper sx={{ p: 2, borderRadius: 3, mb: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {selectedProductCodes.length} of {results.length} products selected for submission
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="outlined"
              onClick={() => setSelectedProductCodes(results.filter((r) => r.status === 'approval').map((r) => r.productCode))}
              sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.7rem' }}>
              Select All Pre Approve
            </Button>
            <Button size="small" variant="outlined"
              onClick={() => setSelectedProductCodes(allEligible.map((r) => r.productCode))}
              sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.7rem' }}>
              Select Pre Approve + Refer
            </Button>
            <Button size="small" variant="outlined" color="error"
              onClick={() => setSelectedProductCodes([])}
              sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.7rem' }}>
              Clear
            </Button>
          </Box>
        </Paper>

        {/* Cross-sell prompt if all failed */}
        {screeningResult && screeningResult.summary.approval === 0 && screeningResult.summary.refer === 0 && loanConfig.allowCrossSell && (
          <Alert severity="warning" sx={{ mb: 2.5, borderRadius: 2 }}>
            <strong>All products failed screening.</strong> Consider these alternatives: {loanConfig.crossSellOptions.map((o) => CROSS_SELL_OPTIONS.find((c) => c.key === o)?.label).filter(Boolean).join(', ') || 'Credit Card / Money Lender / Advisory'}
          </Alert>
        )}

        {/* Product cards grouped by category */}
        {Object.entries(grouped).map(([categoryKey, items]) => {
          const cat = PRODUCT_CATEGORIES.find((c) => c.key === categoryKey);
          return (
            <Box key={categoryKey} sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: cat?.color || PRIMARY, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 4, height: 18, backgroundColor: cat?.color || PRIMARY, borderRadius: 1 }} />
                {cat?.label || categoryKey}
                <Chip label={`${items.length}`} size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }} />
              </Typography>
              <Grid container spacing={2}>
                {items.map((p) => {
                  const sc = screeningStatusColors[p.status] || screeningStatusColors.direct_apply;
                  const StatusIcon = sc.icon;
                  const selected = selectedProductCodes.includes(p.productCode);
                  const canSelect = isSelectable(p.status);
                  return (
                    <Grid item xs={12} sm={6} md={4} key={p.productCode}>
                      <Card sx={{
                        borderRadius: 2, height: '100%',
                        border: selected ? `2px solid ${SECONDARY}` : '1px solid #E0E0E0',
                        backgroundColor: !canSelect ? '#FAFAFA' : selected ? '#F0FFF4' : 'white',
                        opacity: !canSelect ? 0.7 : 1,
                        cursor: canSelect ? 'pointer' : 'not-allowed',
                        '&:hover': canSelect ? { boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } : {},
                        transition: 'all 0.2s',
                      }}
                      onClick={() => {
                        if (!canSelect) return;
                        setSelectedProductCodes((prev) =>
                          prev.includes(p.productCode)
                            ? prev.filter((c) => c !== p.productCode)
                            : [...prev, p.productCode]
                        );
                      }}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#90A4AE', display: 'block' }}>
                                {p.productCode}
                              </Typography>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: PRIMARY, lineHeight: 1.2 }}>
                                {p.lenderName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {p.productName}
                              </Typography>
                            </Box>
                            <Checkbox
                              size="small" checked={selected} disabled={!canSelect}
                              sx={{ p: 0, '&.Mui-checked': { color: SECONDARY } }}
                            />
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                            <Chip
                              icon={<StatusIcon sx={{ fontSize: 14, color: `${sc.color} !important` }} />}
                              label={sc.label} size="small"
                              sx={{
                                backgroundColor: sc.bg, color: sc.color,
                                fontWeight: 700, fontSize: '0.65rem',
                              }}
                            />
                            {p.score !== null && p.score !== undefined && (
                              <Chip label={`Score: ${p.score}`} size="small"
                                sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }} />
                            )}
                          </Box>
                          {p.score !== null && p.score !== undefined && (
                            <LinearProgress
                              variant="determinate" value={p.score}
                              sx={{
                                height: 4, borderRadius: 2, mb: 1,
                                '& .MuiLinearProgress-bar': { backgroundColor: sc.color },
                              }}
                            />
                          )}
                          {p.reason && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                              {p.reason}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          );
        })}

        {/* Footer note */}
        <Alert severity="info" sx={{ borderRadius: 2, mt: 2 }}>
          <Typography variant="caption">
            <strong>Note:</strong> Screening results are advisory only and based on data mapping. Final approval is at the bank's discretion. 仅供参考 — some results may have data mapping mismatches.
          </Typography>
        </Alert>
      </Box>
    );
  };

  // ─── Step 5: Review & Submit ────────────────────────────
  const renderStep5 = () => {
    const selectedProducts = (screeningResult?.results || []).filter((r) => selectedProductCodes.includes(r.productCode));
    const purposeLabel = LOAN_PURPOSES.find((p) => p.key === loanConfig.purpose)?.label || loanConfig.purpose;
    const employmentLabel = EMPLOYMENT_TYPES.find((e) => e.key === customer.employmentType)?.label || customer.employmentType;

    return (
      <Box>
        <Grid container spacing={2.5}>
          {/* Customer Summary */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: PRIMARY }}>
                  Customer Information
                </Typography>
                <IconButton size="small" onClick={() => setActiveStep(0)}>
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
              <Grid container spacing={1.5}>
                <Grid item xs={12}><Typography variant="caption" color="text.secondary">Full Name</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{customer.fullName}</Typography></Grid>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary">IC Number</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{customer.icNumber}</Typography></Grid>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary">Phone</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{customer.phone}</Typography></Grid>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary">Gender</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>{customer.gender || '—'}</Typography></Grid>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary">Email</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{customer.email || '—'}</Typography></Grid>
                <Grid item xs={12}><Typography variant="caption" color="text.secondary">Address</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {[customer.address, customer.addressLine2, customer.city, customer.addressState, customer.addressPostcode].filter(Boolean).join(', ') || '—'}
                  </Typography></Grid>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary">State</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{customer.state || '—'}</Typography></Grid>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary">Postcode</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{customer.postcode || '—'}</Typography></Grid>
                <Grid item xs={12}><Divider /></Grid>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary">Employment Type</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{employmentLabel}</Typography></Grid>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary">Monthly Income</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: PRIMARY }}>{formatRM(customer.monthlyIncome)}</Typography></Grid>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary">Company</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{customer.companyName || '—'}</Typography></Grid>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary">Position</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{customer.position || '—'}</Typography></Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Loan Configuration Summary */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: PRIMARY }}>
                  Loan Configuration
                </Typography>
                <IconButton size="small" onClick={() => setActiveStep(2)}>
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
              <Grid container spacing={1.5}>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary">Loan Amount</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: PRIMARY }}>{formatRM(loanConfig.loanAmount)}</Typography></Grid>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary">Tenure</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{loanConfig.tenure} year{loanConfig.tenure > 1 ? 's' : ''}</Typography></Grid>
                <Grid item xs={12}><Typography variant="caption" color="text.secondary">Purpose</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{purposeLabel}</Typography></Grid>
                <Grid item xs={12}><Typography variant="caption" color="text.secondary">Process Mode</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{loanConfig.processMode === 'ai_screening' ? 'AI Screening' : 'Direct Apply'}</Typography></Grid>
                <Grid item xs={12}><Typography variant="caption" color="text.secondary">Cross-Sell Allowed</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{loanConfig.allowCrossSell ? 'Yes' : 'No'}</Typography></Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Selected products */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: PRIMARY, mb: 2 }}>
                Selected Products ({selectedProducts.length})
              </Typography>
              {selectedProducts.length === 0 ? (
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  No products selected — go back to Step 4 and select products to submit.
                </Alert>
              ) : (
                <Box>
                  {selectedProducts.map((p) => {
                    const sc = screeningStatusColors[p.status] || screeningStatusColors.direct_apply;
                    return (
                      <Box key={p.productCode} sx={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        p: 1.5, mb: 1, borderRadius: 2, backgroundColor: '#F5F7FA',
                      }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{p.lenderName} — {p.productName}</Typography>
                          <Typography variant="caption" color="text.secondary">{p.productCode}</Typography>
                        </Box>
                        <Chip label={sc.label} size="small" sx={{ backgroundColor: sc.bg, color: sc.color, fontWeight: 700, fontSize: '0.65rem' }} />
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Paper>
          </Grid>

          {/* PDPA Consent */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', backgroundColor: '#FFFDE7' }}>
              <FormControlLabel
                control={<Checkbox checked={pdpaConsent} onChange={(e) => setPdpaConsent(e.target.checked)} />}
                label={<Typography variant="body2">
                  I confirm that the customer has provided informed consent for iMoney to process their personal data for loan application purposes (PDPA 2010 / Personal Data Protection Act).
                </Typography>}
              />
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  };

  // ─── Render ─────────────────────────────────────────────
  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: PRIMARY }}>
            Submit Application
          </Typography>
          <Typography variant="body2" color="text.secondary">
            5-step wizard with AI screening and multi-product submission
          </Typography>
        </Box>
        <Button startIcon={<SaveIcon />} onClick={handleSaveDraft}
          sx={{ borderRadius: 2, textTransform: 'none', color: PRIMARY }}>
          Save Draft
        </Button>
      </Box>

      {/* Stepper */}
      <Paper sx={{ p: 3, borderRadius: 3, mb: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((step, idx) => {
            const StepIcon = step.icon;
            return (
              <Step key={step.label}>
                <StepLabel StepIconComponent={() => (
                  <Avatar sx={{
                    width: 36, height: 36,
                    backgroundColor: idx <= activeStep ? PRIMARY : '#E0E0E0',
                    color: idx <= activeStep ? 'white' : '#90A4AE',
                  }}>
                    <StepIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                )}>
                  <Typography variant="caption" sx={{ fontWeight: idx === activeStep ? 700 : 500 }}>
                    {step.label}
                  </Typography>
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </Paper>

      {/* Step content */}
      <Box sx={{ mb: 3 }}>
        {activeStep === 0 && renderStep1()}
        {activeStep === 1 && renderStep2()}
        {activeStep === 2 && renderStep3()}
        {activeStep === 3 && renderStep4()}
        {activeStep === 4 && renderStep5()}
      </Box>

      {/* Navigation */}
      <Paper sx={{ p: 2, borderRadius: 3, display: 'flex', justifyContent: 'space-between', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        <Button onClick={handleBack} disabled={activeStep === 0}
          sx={{ textTransform: 'none', borderRadius: 2 }}>
          Back
        </Button>
        {activeStep < steps.length - 1 ? (
          <Button variant="contained" onClick={handleNext}
            sx={{ borderRadius: 2, textTransform: 'none', backgroundColor: PRIMARY, px: 4 }}>
            Next
          </Button>
        ) : (
          <Button variant="contained" onClick={handleSubmit}
            disabled={selectedProductCodes.length === 0 || !pdpaConsent}
            sx={{ borderRadius: 2, textTransform: 'none', backgroundColor: SECONDARY, px: 4 }}>
            Submit to iMortgage2u
          </Button>
        )}
      </Paper>

      {/* Success Dialog */}
      <Dialog open={successDialog} onClose={() => {}} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 1 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: SECONDARY }} />
            <Box sx={{ fontWeight: 700, fontSize: '1.4rem' }}>Submission Successful!</Box>
            <Box component="span" sx={{ fontSize: '0.85rem', color: 'text.secondary', display: 'block', fontWeight: 400 }}>
              {submittedCases.length} case{submittedCases.length > 1 ? 's' : ''} submitted to iMortgage2u
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
            Cases have been forwarded to iMortgage2u. You will receive notifications as the status updates.
          </Alert>
          {submittedCases.map((c) => (
            <Box key={c.id} sx={{
              p: 2, mb: 1, borderRadius: 2, backgroundColor: '#F5F7FA',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <Box>
                <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 700, color: PRIMARY }}>{c.id}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.bank} — {c.productName}</Typography>
                <Typography variant="caption" color="text.secondary">{formatRM(c.amount)}</Typography>
              </Box>
              <Tooltip title="Copy case reference">
                <IconButton size="small" onClick={() => {
                  navigator.clipboard?.writeText(c.id);
                  showSnack('Case reference copied');
                }}>
                  <ContentCopyIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button startIcon={<WhatsAppIcon />}
            onClick={() => {
              const phone = customer.phone.replace(/[^0-9]/g, '');
              const refs = submittedCases.map((c) => c.id).join(', ');
              const msg = encodeURIComponent(`Hi ${customer.fullName}, your iMoney case reference${submittedCases.length > 1 ? 's are' : ' is'}: ${refs}. We will update you on the application progress.`);
              window.open(`https://wa.me/${phone}?text=${msg}`, '_blank');
            }}
            sx={{ textTransform: 'none', color: '#25D366' }}>
            Send to Customer via WhatsApp
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleResetWizard} sx={{ textTransform: 'none' }}>
              New Submission
            </Button>
            <Button variant="contained" onClick={() => navigate('/partner/applications')}
              sx={{ borderRadius: 2, textTransform: 'none', backgroundColor: PRIMARY }}>
              View in Application Case
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3500}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })} sx={{ borderRadius: 2 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
